import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import SocialConnectBanner from "../components/SocialConnectBanner";
import { Save, Check, Sparkles, Building2, BadgeCheck, Clock, X, Search } from "lucide-react";

const CATEGORIES = ["Apparel","Nutrition","Fitness Tech","Recovery","Energy Drink","Eyewear","Footwear","Gaming","Crypto/Fintech","Lifestyle","Auto","Local Business"];

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [linkRequests, setLinkRequests] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const reloadLinks = async () => {
    try {
      const { data } = await api.get("/athlete/org-link-requests");
      setLinkRequests(data.requests || []);
    } catch { /* athletes-only endpoint — silent fail for non-athletes */ }
  };

  useEffect(() => {
    api.get("/profile").then(({data}) => setProfile({ target_sponsor_categories: [], ...data }));
    reloadLinks();
  }, []);

  if (!profile) return <DashboardLayout><div className="p-12 label-eyebrow">Loading…</div></DashboardLayout>;

  const upd = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const toggleCat = (c) => upd("target_sponsor_categories",
    (profile.target_sponsor_categories || []).includes(c)
      ? profile.target_sponsor_categories.filter(x => x !== c)
      : [...(profile.target_sponsor_categories || []), c]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      const { data } = await api.put("/profile", profile);
      setProfile({ target_sponsor_categories: [], ...data });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  // Find the most relevant link request for a given kind. Approved beats
  // pending beats declined — we only ever show ONE status badge per field.
  const linkFor = (kind) => {
    const rows = linkRequests.filter(r => r.org_kind === kind);
    return rows.find(r => r.status === "approved")
        || rows.find(r => r.status === "pending")
        || rows.find(r => r.status === "declined")
        || null;
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-12">
        <div className="flex justify-between items-end mb-10 flex-wrap gap-3">
          <div>
            <div className="label-eyebrow">Athlete</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="profile-title">Profile</h1>
          </div>
          <button onClick={save} disabled={saving} className="btn-volt inline-flex items-center gap-2" data-testid="profile-save-btn">
            {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving…" : saved ? "Saved" : "Save profile"}
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Section title="Identity">
            <Field label="Full name"><Input value={profile.full_name} onChange={(v)=>upd("full_name", v)} testid="p-fullname" /></Field>
            <Field label="Location"><Input value={profile.location} onChange={(v)=>upd("location", v)} testid="p-location" /></Field>
            <Field label="Sport"><Input value={profile.sport} onChange={(v)=>upd("sport", v)} testid="p-sport" /></Field>
            <Field label="Level"><Input value={profile.level} onChange={(v)=>upd("level", v)} testid="p-level" /></Field>
          </Section>

          <Section title="Representation & club">
            <OrgLinkField
              label="Agency"
              kind="agency"
              value={profile.agency_name}
              onChange={(v) => upd("agency_name", v)}
              link={linkFor("agency")}
              onRequested={reloadLinks}
              onUnlinked={reloadLinks}
              testid="p-agency"
            />
            <OrgLinkField
              label="Club"
              kind="club"
              value={profile.club_name}
              onChange={(v) => upd("club_name", v)}
              link={linkFor("club")}
              onRequested={reloadLinks}
              onUnlinked={reloadLinks}
              testid="p-club-name"
            />
            <Field label="Legacy 'club' (free text — kept for compatibility)">
              <Input value={profile.club} onChange={(v)=>upd("club", v)} testid="p-club" />
            </Field>
          </Section>

          <Section title="Physical stats (optional)">
            <Field label="Height (cm)">
              <Input
                value={profile.height_cm == null ? "" : String(profile.height_cm)}
                onChange={(v) => {
                  const n = parseInt(v, 10);
                  upd("height_cm", Number.isFinite(n) && n > 0 ? n : null);
                }}
                testid="p-height"
              />
            </Field>
            <Field label="Shoe size (e.g. UK 9, US 10.5, EU 43)">
              <Input value={profile.shoe_size} onChange={(v)=>upd("shoe_size", v)} testid="p-shoesize" />
            </Field>
            <div className="text-xs text-ink-muted leading-relaxed">
              Apparel and footwear sponsors look at this. Leaving it blank is fine — we just can&apos;t pre-fill kit details on those proposals.
            </div>
          </Section>

          <Section title="Audience" wide>
            <SocialConnectBanner />
            <Field label="Total audience size"><Input value={profile.audience_size} onChange={(v)=>upd("audience_size", v)} testid="p-audience" /></Field>
            <Field label="Instagram URL"><Input value={profile.instagram_url} onChange={(v)=>upd("instagram_url", v)} testid="p-ig" /></Field>
            <Field label="TikTok URL"><Input value={profile.tiktok_url} onChange={(v)=>upd("tiktok_url", v)} testid="p-tt" /></Field>
            <Field label="YouTube URL"><Input value={profile.youtube_url} onChange={(v)=>upd("youtube_url", v)} testid="p-yt" /></Field>
            <Field label="LinkedIn URL"><Input value={profile.linkedin_url} onChange={(v)=>upd("linkedin_url", v)} testid="p-li" /></Field>
          </Section>

          <Section title="Story" wide>
            <Field label="Achievements"><Textarea rows={3} value={profile.achievements} onChange={(v)=>upd("achievements", v)} testid="p-achievements" /></Field>
            <Field label="Personal story"><Textarea rows={5} value={profile.personal_story} onChange={(v)=>upd("personal_story", v)} testid="p-story" /></Field>
            <Field label="Sponsorship goals"><Textarea rows={3} value={profile.sponsorship_goals} onChange={(v)=>upd("sponsorship_goals", v)} testid="p-goals" /></Field>
            <Field label="Bio (manual override)"><Textarea rows={3} value={profile.bio} onChange={(v)=>upd("bio", v)} testid="p-bio" /></Field>
          </Section>

          <Section title="Target brand categories" wide>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(c => (
                <button key={c} type="button" onClick={()=>toggleCat(c)} data-testid={`p-cat-${c}`}
                  className={`px-3 py-2 border text-xs uppercase tracking-widest ${(profile.target_sponsor_categories || []).includes(c) ? "border-volt text-volt" : "border-line text-ink-muted hover:text-white"}`}>
                  {c}
                </button>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </DashboardLayout>
  );
}

// ----- Org-link field with autocomplete + status badge ---------------------
function OrgLinkField({ label, kind, value, onChange, link, onRequested, onUnlinked, testid }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);
  const blurRef = useRef(null);

  const handleChange = (v) => {
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v || v.trim().length < 2) {
      setSuggestions([]); setShowSuggest(false); return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/orgs/search?q=${encodeURIComponent(v)}&kind=${kind}`);
        setSuggestions(data.results || []);
        setShowSuggest(true);
      } finally { setSearching(false); }
    }, 220);
  };

  const sendRequest = async (org) => {
    setBusy(true);
    try {
      onChange(org.name); // sync the visible text to the matched org name
      await api.post("/athlete/org-link-requests", {
        org_id: org.id,
        org_kind: kind,
        typed_name: org.name,
      });
      await onRequested?.();
      setShowSuggest(false);
    } finally { setBusy(false); }
  };

  const unlink = async () => {
    if (!link) return;
    setBusy(true);
    try {
      await api.delete(`/athlete/org-link-requests/${link.id}`);
      await onUnlinked?.();
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={testid}>
      <label className="label-eyebrow block mb-2">{label}</label>
      <div className="relative">
        <input
          value={value || ""}
          onChange={(e)=>handleChange(e.target.value)}
          onFocus={() => suggestions.length && setShowSuggest(true)}
          onBlur={() => { blurRef.current = setTimeout(() => setShowSuggest(false), 150); }}
          data-testid={`${testid}-input`}
          placeholder={`Type your ${label.toLowerCase()} name…`}
          className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white pr-10"
        />
        {searching && <Search className="absolute right-3 top-3 w-4 h-4 text-ink-muted animate-pulse" />}
        {showSuggest && suggestions.length > 0 && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-bg border border-line shadow-2xl max-h-64 overflow-auto"
               data-testid={`${testid}-suggest`}
               onMouseDown={() => clearTimeout(blurRef.current)}>
            {suggestions.map(s => (
              <button key={s.id} type="button" onClick={() => sendRequest(s)}
                disabled={busy}
                data-testid={`${testid}-suggest-${s.id}`}
                className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-line last:border-b-0 flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-white">
                  <Building2 className="w-3.5 h-3.5 text-ink-muted shrink-0" />
                  <span className="truncate">{s.name}</span>
                  {s.verified && <BadgeCheck className="w-3.5 h-3.5 text-volt shrink-0" />}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-volt shrink-0">Request link</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Link status badge — sits below the input */}
      {link && (
        <div className="mt-2 flex items-center gap-2 text-xs" data-testid={`${testid}-status`}>
          {link.status === "approved" && (
            <>
              <BadgeCheck className="w-4 h-4 text-volt" />
              <span className="text-volt font-medium">Verified</span>
              <span className="text-ink-muted">· linked to {link.org_name}</span>
              <button onClick={unlink} disabled={busy}
                className="ml-auto text-ink-muted hover:text-white inline-flex items-center gap-1"
                data-testid={`${testid}-unlink-btn`}>
                <X className="w-3 h-3" /> Unlink
              </button>
            </>
          )}
          {link.status === "pending" && (
            <>
              <Clock className="w-4 h-4 text-ink-muted animate-pulse" />
              <span className="text-ink-muted">Awaiting approval from {link.org_name}</span>
              <button onClick={unlink} disabled={busy}
                className="ml-auto text-ink-muted hover:text-white inline-flex items-center gap-1"
                data-testid={`${testid}-cancel-btn`}>
                <X className="w-3 h-3" /> Cancel
              </button>
            </>
          )}
          {link.status === "declined" && (
            <>
              <Sparkles className="w-4 h-4 text-ink-muted" />
              <span className="text-ink-muted">Request was declined. You can re-request or just leave the name typed.</span>
              <button onClick={unlink} disabled={busy}
                className="ml-auto text-ink-muted hover:text-white inline-flex items-center gap-1"
                data-testid={`${testid}-clear-btn`}>
                <X className="w-3 h-3" /> Clear
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children, wide }) {
  return <div className={`surface p-6 ${wide ? "md:col-span-2" : ""}`}><div className="label-eyebrow mb-4">{title}</div><div className="space-y-4">{children}</div></div>;
}
function Field({ label, children }) { return <div><label className="label-eyebrow block mb-2">{label}</label>{children}</div>; }
function Input({ value, onChange, testid }) { return <input value={value || ""} onChange={(e)=>onChange(e.target.value)} data-testid={testid} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white" />; }
function Textarea({ value, onChange, rows = 3, testid }) { return <textarea rows={rows} value={value || ""} onChange={(e)=>onChange(e.target.value)} data-testid={testid} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white resize-none" />; }
